import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, useForm, UseFormSetError } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { useArchivedSegmentReferences } from "../hooks/useArchivedSegmentReferences";
import type {
  ConfigurationFormValues,
  ModuleConfiguration,
  ModuleConfigurationSchema,
  ModuleDependency,
  ModuleMessages,
} from "../types";
import { CascadeDisableConfirmationDialog } from "./CascadeDisableConfirmationDialog";
import { ConfigurationField } from "./ConfigurationField";
import { buildConfigurationSchema } from "../utils/build-configuration-schema";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import { Label } from "@/components/ui/v2/label";
import { Switch } from "@/components/ui/v2/switch";
import { UnsavedChangesDialog } from "@/components/ui/v2/unsaved-changes-dialog";
import type { ConfigurationContext } from "@/types/configuration";

type ConfigurationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  isActive: boolean;
  schemaQuery: {
    data?: ModuleConfigurationSchema;
    isPending: boolean;
  };
  configQuery: {
    data?: ModuleConfiguration;
    isPending: boolean;
  };
  onSave: (
    configurations: ModuleConfiguration,
    isActive: boolean,
  ) => Promise<void>;
  isLoading?: boolean;
  context: ConfigurationContext;
  /** The module being configured (for module-scoped reference data, e.g. workflow statuses). */
  moduleName?: string;
  dependencies?: ModuleDependency[];
  hasUnmetDependencies?: boolean;
  messages?: ModuleMessages;
  dependentModules?: ModuleDependency[];
  hasActiveDependentModules?: boolean;
  /**
   * Whether to show the enable/disable toggle. Integrations enable via the card switch, so their
   * configuration dialog is configure-only (`false`). Module dialogs still toggle here (`true`).
   */
  showEnableToggle?: boolean;
  renderAboveFields?: React.ReactNode;
  renderFooter?: React.ReactNode;
  onEnabledChange?: (enabled: boolean) => void;
  onFormMethodsReady?: (
    setError: UseFormSetError<ConfigurationFormValues>,
    getValues: () => ConfigurationFormValues,
  ) => void;
};

export function ConfigurationDialog({
  open,
  onOpenChange,
  title,
  description,
  isActive: initialIsActive,
  schemaQuery,
  configQuery,
  onSave,
  isLoading: externalIsLoading,
  context,
  moduleName,
  dependencies = [],
  hasUnmetDependencies = false,
  messages,
  dependentModules = [],
  hasActiveDependentModules = false,
  showEnableToggle = true,
  renderAboveFields,
  renderFooter,
  onEnabledChange,
  onFormMethodsReady,
}: ConfigurationDialogProps) {
  const { t, i18n } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(initialIsActive);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCascadeConfirmation, setShowCascadeConfirmation] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    configurations: Record<string, string>;
    isActive: boolean;
  } | null>(null);

  // Build validation schema based on current enabled state
  const validationSchema = schemaQuery.data?.["form-fields"]
    ? buildConfigurationSchema(schemaQuery.data["form-fields"], isEnabled, t)
    : undefined;

  const methods = useForm<ConfigurationFormValues>({
    mode: "onChange",
    resolver: validationSchema ? zodResolver(validationSchema) : undefined,
    defaultValues: {},
  });

  const {
    reset,
    setError,
    getValues,
    formState: { isDirty, isSubmitting },
  } = methods;

  // Expose form methods to parent component
  useEffect(() => {
    if (onFormMethodsReady) {
      onFormMethodsReady(setError, getValues);
    }
  }, [onFormMethodsReady, setError, getValues]);

  // Track if save is in progress (form submission + external loading)
  const isSaving = isSubmitting || externalIsLoading || false;

  const archivedSegmentNames = useArchivedSegmentReferences(
    methods.control,
    schemaQuery.data?.["form-fields"],
    context.level === "client" ? context.clientId : undefined,
  );
  const isBlockedByArchivedSegment =
    showEnableToggle && archivedSegmentNames.length > 0;

  // Reset form when configuration and schema load
  useEffect(() => {
    // If schema has form fields, populate form with config data
    if (
      schemaQuery.data?.["form-fields"] &&
      schemaQuery.data["form-fields"].length > 0
    ) {
      const configData = configQuery.data || {};
      const mergedValues = schemaQuery.data["form-fields"].reduce(
        (acc, field) => {
          const configValue =
            configData[field.key] ||
            Object.entries(configData).find(
              ([key]) => key.toLowerCase() === field.key.toLowerCase(),
            )?.[1];

          if (field.type === "boolean") {
            const stringValue = String(configValue || "");
            // Use default-value from schema ONLY if no DB value exists and module is enabled
            // Form values always win - if user has set a value, it's already in the form state
            if (!configValue && field["default-value"] && isEnabled) {
              acc[field.key] = field["default-value"];
            } else {
              const boolValue = stringValue === "true";
              acc[field.key] = boolValue ? "true" : "false";
            }
          } else if (field.type === "multi-input-list") {
            if (configValue && typeof configValue === "string") {
              try {
                const parsed = JSON.parse(configValue);
                if (Array.isArray(parsed)) {
                  acc[field.key] = parsed;
                } else {
                  acc[field.key] = [];
                }
              } catch {
                acc[field.key] = [];
              }
            } else {
              acc[field.key] = [];
            }
          } else {
            // Use default-value from schema ONLY if no DB value exists and module is enabled
            // Form values always win - if user has set a value, it's already in the form state
            if (!configValue && field["default-value"] && isEnabled) {
              acc[field.key] = field["default-value"];
            } else {
              acc[field.key] = String(configValue || "");
            }
          }

          return acc;
        },
        {} as ConfigurationFormValues,
      );

      reset(mergedValues);
    } else if (!schemaQuery.isPending && !configQuery.isPending) {
      // If no schema fields, ensure form is reset to empty state
      reset({});
    }
  }, [
    configQuery.data,
    configQuery.isPending,
    schemaQuery.data,
    schemaQuery.isPending,
    reset,
    isEnabled,
  ]);

  // Reset enabled state when dialog opens
  useEffect(() => {
    if (open) {
      setIsEnabled(initialIsActive);
    }
  }, [open, initialIsActive]);

  // Reset fields with show-when-enabled when module enabled/disabled
  useEffect(() => {
    const formFields = schemaQuery.data?.["form-fields"];
    if (!formFields) return;

    const { setValue, getValues } = methods;
    const currentValues = getValues() as Record<string, string>;
    const configData = configQuery.data || {};

    formFields
      .filter((field) => field["show-when-enabled"])
      .forEach((field) => {
        if (!isEnabled) {
          // When disabling, reset fields to empty/false
          if (currentValues[field.key]) {
            if (field.type === "boolean") {
              setValue(field.key, "false");
            } else {
              setValue(field.key, "");
            }
          }
        } else {
          // When enabling, only apply default if:
          // 1. No DB value exists (configData doesn't have it)
          // 2. No form value exists (user hasn't set it)
          // This ensures form values always win - if user explicitly set a value, we respect it
          const hasDbValue =
            configData[field.key] !== undefined &&
            configData[field.key] !== null &&
            configData[field.key] !== "";
          const hasFormValue =
            currentValues[field.key] !== undefined &&
            currentValues[field.key] !== null &&
            currentValues[field.key] !== "";

          if (!hasDbValue && !hasFormValue && field["default-value"]) {
            setValue(field.key, field["default-value"]);
          }
        }
      });
  }, [isEnabled, methods, schemaQuery.data, configQuery.data]);

  const handleOpenChange = (newOpen: boolean) => {
    if (showConfirmation) {
      return;
    }

    if (!newOpen && (isDirty || isEnabled !== initialIsActive)) {
      setShowConfirmation(true);
    } else {
      onOpenChange(newOpen);
    }
  };

  const handleConfirmLeave = () => {
    setShowConfirmation(false);
    reset();
    setIsEnabled(initialIsActive);
    onOpenChange(false);
  };

  // When user toggles enable/disable
  const handleEnabledChange = (checked: boolean) => {
    setIsEnabled(checked);
    onEnabledChange?.(checked);
  };

  const onSubmit = methods.handleSubmit(
    async (data) => {
      const formFields = schemaQuery.data?.["form-fields"] || [];
      const fieldsToExclude =
        formFields
          .filter((field) => field["show-when-enabled"] && !isEnabled)
          .map((field) => field.key) || [];

      const configurations = Object.entries(data).reduce(
        (acc, [key, value]) => {
          if (fieldsToExclude.includes(key)) {
            return acc;
          }

          const fieldDef = formFields.find((f) => f.key === key);

          if (fieldDef?.type === "multi-input-list") {
            if (Array.isArray(value) && value.length > 0) {
              const serializedValue = value.map((item, index) => ({
                id: item.id,
                values: {
                  ...item.values,
                  Index: index,
                },
              }));
              acc[key] = JSON.stringify(serializedValue);
            } else if (value !== undefined && value !== "") {
              acc[key] =
                typeof value === "string" ? value : JSON.stringify(value);
            }
          } else if (value !== undefined && value !== "") {
            let processedValue = (value as string).trim();
            // Strip trailing slash from ApiUrl fields
            if (key === "ApiUrl" && processedValue.endsWith("/")) {
              processedValue = processedValue.slice(0, -1);
            }
            acc[key] = processedValue;
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      // Check if disabling and has active dependents - show confirmation dialog
      const isDisabling = initialIsActive && !isEnabled;
      if (
        isDisabling &&
        hasActiveDependentModules &&
        dependentModules.some((d) => d.isSatisfied)
      ) {
        setPendingSaveData({ configurations, isActive: isEnabled });
        setShowCascadeConfirmation(true);
        return;
      }

      await performSave(configurations, isEnabled, data);
    },
    (errors) => {
      // Log validation errors for debugging
      console.error("Form validation errors:", errors);
    },
  );

  const performSave = async (
    configurations: Record<string, string>,
    isActive: boolean,
    formData: ConfigurationFormValues,
  ) => {
    await onSave(configurations, isActive);
    onOpenChange(false);
    reset(formData);
  };

  const handleCascadeConfirm = async () => {
    setShowCascadeConfirmation(false);
    if (pendingSaveData) {
      const formData = getValues();
      await performSave(
        pendingSaveData.configurations,
        pendingSaveData.isActive,
        formData,
      );
      setPendingSaveData(null);
    }
  };

  const isLoading = schemaQuery.isPending || configQuery.isPending;

  // Translate description if it's a translation key
  const translatedDescription =
    description && i18n.exists(description)
      ? t(description)
      : description || t("integrations.modules.configure.description");

  // Translate title if it's a translation key
  const translatedTitle = title && i18n.exists(title) ? t(title) : title;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-auto">
          <DialogHeader>
            <DialogTitle>{translatedTitle}</DialogTitle>
            <DialogDescription>{translatedDescription}</DialogDescription>
          </DialogHeader>
          <FormProvider {...methods}>
            <form onSubmit={onSubmit} className="space-y-6 overflow-hidden">
              {/* Generic Info Alert for Enable/Disable Messages */}
              {messages?.enable && !isEnabled && (
                <Alert variant="default">
                  <AlertTitle>
                    {t("integrations.modules.configure.message.title")}
                  </AlertTitle>
                  <AlertDescription>
                    {i18n.exists(messages.enable)
                      ? t(messages.enable)
                      : messages.enable}
                  </AlertDescription>
                </Alert>
              )}

              {messages?.disable && isEnabled && (
                <Alert variant="default">
                  <AlertTitle>
                    {t("integrations.modules.configure.message.title")}
                  </AlertTitle>
                  <AlertDescription>
                    {i18n.exists(messages.disable)
                      ? t(messages.disable)
                      : messages.disable}
                  </AlertDescription>
                </Alert>
              )}

              {/* Dependency Warning */}
              {hasUnmetDependencies && dependencies.length > 0 && (
                <Alert variant="warning">
                  <AlertTitle>
                    {t("integrations.modules.configure.dependencies.title")}
                  </AlertTitle>
                  <AlertDescription>
                    {dependencies.length === 1 ? (
                      <p>
                        <Trans
                          i18nKey="integrations.modules.configure.dependencies.description"
                          components={{
                            1: <b />,
                            2: <b />,
                          }}
                          values={{
                            moduleName: t(dependencies[0].moduleDisplayName),
                            integrationName: dependencies[0]
                              .integrationDisplayName
                              ? t(dependencies[0].integrationDisplayName)
                              : "",
                          }}
                        />
                      </p>
                    ) : (
                      <>
                        <p className="mb-2">
                          <Trans
                            i18nKey="integrations.modules.configure.dependencies.multipleDescription"
                            components={{
                              1: <b />,
                              2: <b />,
                            }}
                          />
                        </p>

                        <ul className="list-disc list-inside space-y-1">
                          {dependencies.map((dep, index) => (
                            <li key={index}>
                              {dep.moduleDisplayName} from{" "}
                              {dep.integrationDisplayName}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    <p className="mt-2 font-medium">
                      {t(
                        "integrations.modules.configure.dependencies.enableBlocked",
                      )}
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {isBlockedByArchivedSegment && (
                <Alert
                  variant="warning"
                  data-testid="module-archived-segment-banner"
                >
                  <AlertTitle>
                    {t("integrations.modules.configure.archivedSegment.title")}
                  </AlertTitle>
                  <AlertDescription>
                    {t(
                      "integrations.modules.configure.archivedSegment.description",
                      {
                        segments: archivedSegmentNames.join(", "),
                      },
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Enable/Disable Toggle (hidden for configure-only dialogs, e.g. integrations) */}
              {showEnableToggle && (
                <div className="flex items-center justify-between space-x-2 py-2">
                  <Label htmlFor="configuration-enabled" className="text-base">
                    {t("integrations.modules.configure.enable", {
                      module: translatedTitle,
                    })}
                  </Label>
                  <Switch
                    id="configuration-enabled"
                    checked={isEnabled}
                    onCheckedChange={handleEnabledChange}
                    disabled={
                      isSaving ||
                      (hasUnmetDependencies && !initialIsActive) ||
                      (isBlockedByArchivedSegment && !isEnabled)
                    }
                  />
                </div>
              )}

              {renderAboveFields}

              {/* Configuration Fields */}
              {isLoading ? (
                <div className="space-y-4">
                  <div className="h-10 bg-muted animate-pulse rounded" />
                  <div className="h-10 bg-muted animate-pulse rounded" />
                </div>
              ) : (
                schemaQuery.data?.["form-fields"] &&
                schemaQuery.data["form-fields"].length > 0 && (
                  <div className="space-y-4">
                    {schemaQuery.data["form-fields"]
                      .filter((field) => {
                        if (field["show-when-enabled"] && !isEnabled) {
                          return false;
                        }
                        return true;
                      })
                      .map((field) => (
                        <ConfigurationField
                          key={field.key}
                          field={field}
                          disabled={
                            isSaving ||
                            (field["show-when-enabled"] && !isEnabled)
                          }
                          clientId={
                            context.level === "client"
                              ? context.clientId
                              : undefined
                          }
                          moduleName={moduleName}
                        />
                      ))}
                  </div>
                )
              )}

              {renderFooter !== undefined ? (
                renderFooter
              ) : (
                <DialogFooter className="sm:justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isSaving}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      isSaving ||
                      (hasUnmetDependencies && !initialIsActive) ||
                      (isBlockedByArchivedSegment && isEnabled)
                    }
                  >
                    {isSaving
                      ? t("common.saving", "Saving...")
                      : t("common.save")}
                  </Button>
                </DialogFooter>
              )}
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes confirmation dialog */}
      <UnsavedChangesDialog
        open={showConfirmation}
        onStay={() => setShowConfirmation(false)}
        onLeave={handleConfirmLeave}
      />

      {/* Cascade disable confirmation dialog */}
      <CascadeDisableConfirmationDialog
        open={showCascadeConfirmation}
        onOpenChange={setShowCascadeConfirmation}
        dependentModules={dependentModules}
        onConfirm={handleCascadeConfirm}
      />
    </>
  );
}
